import { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Lock, Plus, Pencil, Trash2, Loader2, LogOut, X, Save,
  TrendingUp, ShoppingBag, DollarSign, PiggyBank, BarChart2,
  Package, ChevronDown, ChevronUp, AlertCircle, Calendar,
  Truck, BookOpen, Table2, ArrowDownCircle, ArrowUpCircle,
  ClipboardList, CheckCircle2, Clock, Wallet
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["Cabos","Carregadores","Powerbank","Fones","Acessórios de Informática","Cartões de Memória","Produtos Seminovos","Aluguel de Starlink Mini"];
const emptyProduct = { name:'', category:'Cabos', price:'', image:'', description:'', in_stock:true };
const SESSION_KEY = 'fastfix_admin_session';
function saveSession(c) { try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(c)); } catch {} }
function loadSession() { try { const r=sessionStorage.getItem(SESSION_KEY); return r?JSON.parse(r):null; } catch { return null; } }
function clearSession() { try { sessionStorage.removeItem(SESSION_KEY); } catch {} }
function makeAuthHeader(u,p) { return { headers: { 'Authorization':'Basic '+btoa(`${u}:${p}`), 'X-Requested-With':'XMLHttpRequest' } }; }
const fmt = (v) => (parseFloat(v)||0).toLocaleString('pt-BR',{style:'currency',currency:'BRL'});

function SummaryCard({ icon, label, value, sub, color }) {
  const c = {
    blue:'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800',
    green:'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
    purple:'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800',
    yellow:'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800',
  };
  return (
    <div className={`rounded-xl border p-4 ${c[color]}`}>
      <div className="flex items-center gap-2 mb-2 opacity-70">{icon}<span className="text-xs font-medium">{label}</span></div>
      <p className="text-xl font-bold">{value}</p>
      {sub && <p className="text-xs mt-1 opacity-70">{sub}</p>}
    </div>
  );
}

function InstallmentForm({ salePrice, onChange }) {
  const total = parseFloat(salePrice) || 0;
  const todayStr = new Date().toISOString().split('T')[0];
  const [entrada, setEntrada] = useState('0');
  const [numParcelas, setNumParcelas] = useState(2);
  const [saleDate, setSaleDate] = useState(todayStr);
  const [parcelas, setParcelas] = useState([]);
  const onChangeRef = useRef(onChange);
  useEffect(() => { onChangeRef.current = onChange; });

  useEffect(() => {
    const restante = total - (parseFloat(entrada) || 0);
    const valor = restante > 0 ? (restante / numParcelas).toFixed(2) : '0.00';
    const base = new Date(saleDate + 'T00:00:00');
    const p = Array.from({ length: numParcelas }, (_, i) => {
      const d = new Date(base);
      d.setMonth(d.getMonth() + i + 1);
      return { id: i, data: d.toISOString().split('T')[0], valor, pago: false };
    });
    setParcelas(p);
    onChangeRef.current({ entrada, saleDate, parcelas: p });
  }, [numParcelas, entrada, saleDate, salePrice, total]);

  const updateP = (id, field, val) => {
    const u = parcelas.map(p => p.id === id ? { ...p, [field]: val } : p);
    setParcelas(u);
    onChange({ entrada, saleDate, parcelas: u });
  };

  const totalP = parcelas.reduce((s, p) => s + parseFloat(p.valor || 0), 0);
  const totalG = (parseFloat(entrada) || 0) + totalP;
  const diff = Math.abs(totalG - total);

  return (
    <div className="mt-3 p-3 bg-orange-50 dark:bg-orange-900/10 border border-orange-200 dark:border-orange-800/40 rounded-xl space-y-3">
      <p className="text-xs font-semibold text-orange-700 dark:text-orange-400 flex items-center gap-1">
        <Calendar className="w-3 h-3" /> Detalhes do Parcelamento
      </p>
      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <p className="text-xs text-slate-500 mb-1">Data da Venda</p>
          <input type="date" value={saleDate} onChange={e => setSaleDate(e.target.value)}
            className="text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-1.5 text-slate-900 dark:text-white" />
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-1">Entrada (R$) — pode ser 0</p>
          <input type="number" step="0.01" min="0" value={entrada} onChange={e => setEntrada(e.target.value)}
            className="w-28 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-1.5 text-slate-900 dark:text-white" />
        </div>
        <div>
          <p className="text-xs text-slate-500 mb-1">Parcelas</p>
          <select value={numParcelas} onChange={e => setNumParcelas(parseInt(e.target.value))}
            className="text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-1.5 text-slate-900 dark:text-white">
            {[1,2,3,4,5,6,8,10,12].map(n => <option key={n} value={n}>{n}x</option>)}
          </select>
        </div>
        <div className="pb-1 text-xs text-slate-500">
          Total: <span className={`font-semibold ${diff > 0.05 ? 'text-red-500' : 'text-slate-700 dark:text-slate-300'}`}>{fmt(totalG)}</span>
          {diff > 0.05 && <span className="text-red-400 ml-1">(diverge {fmt(diff)})</span>}
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs bg-white dark:bg-slate-900 rounded-lg px-3 py-2">
        <span className="text-emerald-500 font-bold">✓</span>
        <span className="text-slate-500 w-16 flex-shrink-0">Entrada</span>
        <span className="font-semibold text-emerald-600 dark:text-emerald-400">{fmt(entrada)}</span>
        <span className="ml-auto text-emerald-500 text-xs">recebido no ato</span>
      </div>

      {parcelas.map((p, i) => (
        <div key={p.id} className="flex items-center gap-2 text-xs bg-white dark:bg-slate-900 rounded-lg px-3 py-1.5">
          <span className="text-slate-400 w-14 flex-shrink-0">{i+1}ª parcela</span>
          <input type="date" value={p.data} onChange={e => updateP(p.id,'data',e.target.value)}
            className="text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-slate-700 dark:text-slate-300" />
          <span className="text-slate-400">R$</span>
          <input type="number" step="0.01" value={p.valor} onChange={e => updateP(p.id,'valor',e.target.value)}
            className="w-20 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-2 py-1 text-slate-700 dark:text-slate-300" />
        </div>
      ))}
    </div>
  );
}

function SalesTab({ products, auth }) {
  const [salesData, setSalesData] = useState({});
  const [loadingSales, setLoadingSales] = useState(true);
  const [editingCostId, setEditingCostId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({ qty:1, method:'avista', salePrice:'' });
  const [installData, setInstallData] = useState({ entrada:'0', saleDate:'', parcelas:[] });
  const [remessas, setRemessas] = useState({});
  const [remessaModal, setRemessaModal] = useState(null); // product id
  const [remessaForm, setRemessaForm] = useState({ id:'', custo:'', preco:'', qtd:'1', data: new Date().toISOString().split('T')[0], obs:'' });

  const authHdr = makeAuthHeader(auth.username, auth.password);

  useEffect(() => {
    setLoadingSales(true);
    axios.get(`${API}/sales`, authHdr)
      .then(r => {
        const map = {};
        for (const d of r.data) map[d.product_id] = d;
        setSalesData(map);
        // Carrega remessas de todos os produtos
        Promise.all(r.data.map(d =>
          axios.get(`${API}/remessas/${d.product_id}`, authHdr)
            .then(res => ({ pid: d.product_id, data: res.data }))
            .catch(() => null)
        )).then(results => {
          const rm = {};
          results.filter(Boolean).forEach(r => { rm[r.pid] = r.data.remessas || []; });
          setRemessas(rm);
        });
      })
      .catch(() => toast.error('Erro ao carregar vendas'))
      .finally(() => setLoadingSales(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateCost = async (pid, val) => {
    try {
      await axios.post(`${API}/sales/${pid}/cost`, { cost_price: parseFloat(val)||null }, authHdr);
      setSalesData(prev => ({ ...prev, [pid]: { ...(prev[pid]||{product_id:pid}), cost_price: parseFloat(val)||null } }));
      toast.success('Custo salvo!');
    } catch { toast.error('Erro ao salvar custo'); }
  };

  const addSale = async (pid) => {
    if (!form.salePrice) { toast.error('Informe o preço de venda'); return; }
    const qty = parseInt(form.qty)||1;
    const salePrice = parseFloat(form.salePrice);
    const costPrice = parseFloat(salesData[pid]?.cost_price||0);
    const profit = (salePrice - costPrice) * qty;
    const dateStr = form.method==='aprazo' && installData.saleDate
      ? new Date(installData.saleDate+'T12:00:00').toLocaleDateString('pt-BR')
      : new Date().toLocaleDateString('pt-BR');

    const body = {
      qty, price: salePrice, method: form.method, profit, date: dateStr,
      ...(form.method==='aprazo' ? { entrada: installData.entrada, parcelas: installData.parcelas } : {})
    };

    try {
      const r = await axios.post(`${API}/sales/${pid}/add`, body, authHdr);
      const entry = { ...body, id: r.data.sale_id };
      setSalesData(prev => {
        const ex = prev[pid]||{ product_id:pid, cost_price:null, history:[] };
        return { ...prev, [pid]: { ...ex, history: [entry, ...(ex.history||[])] } };
      });
      setForm({ qty:1, method:'avista', salePrice:'' });
      setInstallData({ entrada:'0', saleDate:'', parcelas:[] });
      setExpandedId(null);
      toast.success('Venda registrada! 💰');
    } catch { toast.error('Erro ao registrar venda'); }
  };

  const toggleParcela = async (pid, sid, parId) => {
    setSalesData(prev => {
      const ex = prev[pid]||{};
      const history = (ex.history||[]).map(s => s.id!==sid ? s : {
        ...s, parcelas: s.parcelas.map(p => p.id===parId ? {...p,pago:!p.pago} : p)
      });
      return { ...prev, [pid]: { ...ex, history } };
    });
    try {
      await axios.patch(`${API}/sales/${pid}/parcela`, { sale_id: sid, parcela_id: parId }, authHdr);
    } catch { toast.error('Erro ao sincronizar parcela'); }
  };

  const deleteSale = async (pid, sid) => {
    setSalesData(prev => {
      const ex = prev[pid]||{};
      return { ...prev, [pid]: { ...ex, history: (ex.history||[]).filter(s=>s.id!==sid) } };
    });
    try {
      await axios.delete(`${API}/sales/${pid}/${sid}`, authHdr);
    } catch { toast.error('Erro ao remover venda'); }
  };

  const saveRemessa = async () => {
    if (!remessaForm.custo || !remessaForm.preco) { toast.error('Informe custo e preço'); return; }
    const pid = remessaModal;
    try {
      const r = await axios.post(`${API}/remessas/${pid}`, remessaForm, authHdr);
      const novaRemessa = r.data.remessa;
      setRemessas(prev => ({ ...prev, [pid]: [novaRemessa, ...(prev[pid]||[]).map(r=>({...r,ativa:false}))] }));
      setSalesData(prev => ({ ...prev, [pid]: { ...(prev[pid]||{product_id:pid}), cost_price: novaRemessa.custo } }));
      setRemessaModal(null);
      setRemessaForm({ id:'', custo:'', preco:'', qtd:'1', data: new Date().toISOString().split('T')[0], obs:'' });
      toast.success('Nova remessa registrada! ✅');
    } catch { toast.error('Erro ao salvar remessa'); }
  };

  const now = new Date();
  const mesAtual = `${String(now.getMonth()+1).padStart(2,'0')}/${now.getFullYear()}`;

  const totals = products.reduce((acc, p) => {
    const h = (salesData[p.id]||{}).history||[];
    const avP = h.filter(s=>s.method==='avista').reduce((s,h)=>s+((h.price||h.salePrice)?((h.price||h.salePrice)*(h.qty||1)):h.profit),0);
    const apR = h.filter(s=>s.method==='aprazo').reduce((s,h)=> s+(parseFloat(h.entrada||0))+(h.parcelas||[]).filter(p=>p.pago).reduce((ps,p)=>ps+parseFloat(p.valor||0),0), 0);
    const apT = h.filter(s=>s.method==='aprazo').reduce((s,h)=> s+(parseFloat(h.entrada||0))+(h.parcelas||[]).reduce((ps,p)=>ps+parseFloat(p.valor||0),0), 0);

    // Filtro do mês atual para lucro e caixa do mês
    const hMes = h.filter(s => {
      const d = s.date || '';
      // date pode ser dd/mm/yyyy ou mm/yyyy
      const parts = d.split('/');
      if (parts.length === 3) return `${parts[1]}/${parts[2]}` === mesAtual;
      if (parts.length === 2) return `${parts[0]}/${parts[1]}` === mesAtual;
      return false;
    });
    const avPMes = hMes.filter(s=>s.method==='avista').reduce((s,h)=>s+((h.price||h.salePrice)?((h.price||h.salePrice)*(h.qty||1)):h.profit),0);
    const apRMes = hMes.filter(s=>s.method==='aprazo').reduce((s,h)=> s+(parseFloat(h.entrada||0))+(h.parcelas||[]).filter(p=>p.pago).reduce((ps,p)=>ps+parseFloat(p.valor||0),0), 0);

    return {
      sold: acc.sold+h.reduce((s,h)=>s+h.qty,0),
      revenue: acc.revenue+h.reduce((s,h)=>s+(h.price||h.salePrice||0)*h.qty,0),
      profit: acc.profit+h.reduce((s,h)=>s+(parseFloat(h.profit)||0),0),
      avista: acc.avista+avP, aprazoR: acc.aprazoR+apR, aprazoT: acc.aprazoT+apT,
      profitMes: acc.profitMes+hMes.reduce((s,h)=>s+(parseFloat(h.profit)||0),0),
      caixaMes: acc.caixaMes+avPMes+apRMes,
    };
  }, { sold:0, revenue:0, profit:0, avista:0, aprazoR:0, aprazoT:0, profitMes:0, caixaMes:0 });

  const caixaReal = totals.avista + totals.aprazoR;
  const aReceber  = totals.aprazoT - totals.aprazoR;

  if (loadingSales) return (
    <div className="flex items-center justify-center py-16 gap-3 text-slate-400">
      <Loader2 className="w-5 h-5 animate-spin"/>
      <span className="text-sm">Carregando vendas...</span>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard icon={<ShoppingBag className="w-5 h-5"/>} label="Total Vendas"  value={totals.sold+' itens'} color="blue"/>
        <SummaryCard icon={<TrendingUp   className="w-5 h-5"/>} label="Receita Total" value={fmt(totals.revenue)}  color="purple"/>
        <SummaryCard icon={<DollarSign   className="w-5 h-5"/>} label="Lucro Total"   value={fmt(totals.profit)}   color="green"/>
        <SummaryCard icon={<PiggyBank    className="w-5 h-5"/>} label="Caixa Real"    value={fmt(caixaReal)} sub={`A receber: ${fmt(aReceber)}`} color="yellow"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[
          { icon:<BarChart2 className="w-5 h-5"/>, bg:'bg-emerald-100 dark:bg-emerald-900/30', tc:'text-emerald-600 dark:text-emerald-400', label:'Lucro À Vista', value:fmt(totals.avista), sub:null },
          { icon:<Calendar  className="w-5 h-5"/>, bg:'bg-orange-100 dark:bg-orange-900/30', tc:'text-orange-500', label:'A Prazo – Recebido', value:fmt(totals.aprazoR), sub:`A receber: ${fmt(aReceber)}` },
          { icon:<Package   className="w-5 h-5"/>, bg:'bg-blue-100 dark:bg-blue-900/30', tc:'text-blue-600 dark:text-blue-400', label:'Reinvestir (40%)', value:fmt(totals.profit*0.4), sub:null },
        ].map((card,i) => (
          <div key={i} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${card.bg} ${card.tc}`}>{card.icon}</div>
            <div>
              <p className="text-xs text-slate-500">{card.label}</p>
              <p className={`text-lg font-bold ${card.tc}`}>{card.value}</p>
              {card.sub && <p className="text-xs text-slate-400">{card.sub}</p>}
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800">
          <h2 className="font-semibold text-slate-900 dark:text-white">Produtos & Vendas</h2>
          <p className="text-xs text-slate-500 mt-0.5">Defina o custo e clique em "+ Venda" para registrar</p>
        </div>
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {products.map(product => {
            const d = salesData[product.id]||{};
            const history = d.history||[];
            const totalProfit = history.reduce((s,h)=>s+h.profit,0);
            const totalSold = history.reduce((s,h)=>s+h.qty,0);
            const isExpanded = expandedId===product.id;
            const isEditingCost = editingCostId===product.id;

            return (
              <div key={product.id}>
                <div className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                  <img src={product.image} alt={product.name} className="w-12 h-12 rounded-lg object-contain bg-slate-100 dark:bg-slate-800 p-1 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 dark:text-white text-sm truncate">{product.name}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${product.in_stock?'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400':'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                        {product.in_stock?'Em estoque':'Esgotado'}
                      </span>
                      <span className="text-xs text-slate-500">Venda: {fmt(product.price)}</span>
                      {d.cost_price && <span className="text-xs text-slate-500">Custo: {fmt(d.cost_price)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 flex-wrap justify-end">
                    <div className="text-right hidden sm:block">
                      <p className="text-xs text-slate-500">{totalSold} vendido(s)</p>
                      <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">{fmt(totalProfit)}</p>
                    </div>
                    {isEditingCost ? (
                      <div className="flex items-center gap-1">
                        <span className="text-xs text-slate-400">R$</span>
                        <input type="number" step="0.01" defaultValue={d.cost_price||''} placeholder="Custo" autoFocus
                          className="w-20 text-xs bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-2 py-1 text-slate-900 dark:text-white"
                          onBlur={e => { updateCost(product.id,e.target.value); setEditingCostId(null); toast.success('Custo salvo!'); }} />
                      </div>
                    ) : (
                      <button onClick={() => setEditingCostId(product.id)}
                        className="text-xs text-blue-500 hover:text-blue-400 border border-blue-500/30 rounded px-2 py-1 hover:bg-blue-500/10 transition-colors">
                        {d.cost_price ? `R$${parseFloat(d.cost_price).toFixed(2)}` : '+ Custo'}
                      </button>
                    )}
                    <button
                      onClick={() => { setRemessaModal(product.id); setRemessaForm({ id:'', custo: d.cost_price||'', preco: product.price||'', qtd:'1', data: new Date().toISOString().split('T')[0], obs:'' }); }}
                      className="flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg transition-colors">
                      📦 Nova Remessa
                    </button>
                    <button
                      onClick={() => { setExpandedId(isExpanded?null:product.id); setForm({qty:1,method:'avista',salePrice:''}); setInstallData({entrada:'0',saleDate:'',parcelas:[]}); }}
                      className="flex items-center gap-1 text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors">
                      + Venda {isExpanded ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>}
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="bg-slate-50 dark:bg-slate-800/50 px-4 py-4 border-t border-slate-100 dark:border-slate-700/50">
                    <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 mb-3">Registrar Nova Venda</p>
                    <div className="flex flex-wrap gap-3 items-end">
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Preço de Venda (R$)</p>
                        <input type="number" step="0.01" value={form.salePrice}
                          onChange={e => setForm(f=>({...f,salePrice:e.target.value}))}
                          placeholder={product.price.toFixed(2)}
                          className="w-28 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white"/>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Qtd</p>
                        <input type="number" min="1" value={form.qty}
                          onChange={e => setForm(f=>({...f,qty:e.target.value}))}
                          className="w-16 text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white"/>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 mb-1">Pagamento</p>
                        <select value={form.method} onChange={e => setForm(f=>({...f,method:e.target.value}))}
                          className="text-sm bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white">
                          <option value="avista">À Vista 💵</option>
                          <option value="aprazo">A Prazo 💳</option>
                        </select>
                      </div>
                      {d.cost_price && form.salePrice && (
                        <div className="text-xs text-slate-500 pb-2">
                          Lucro: <span className={`font-semibold ${(parseFloat(form.salePrice)-parseFloat(d.cost_price))>=0?'text-emerald-500':'text-red-500'}`}>
                            {fmt((parseFloat(form.salePrice)-parseFloat(d.cost_price))*(parseInt(form.qty)||1))}
                          </span>
                        </div>
                      )}
                      {!d.cost_price && (
                        <div className="flex items-center gap-1 text-xs text-amber-500 pb-2">
                          <AlertCircle className="w-3 h-3"/> Defina o custo para calcular lucro real
                        </div>
                      )}
                      <button onClick={() => addSale(product.id)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4 py-2 rounded-lg transition-colors font-medium">
                        Salvar Venda
                      </button>
                    </div>

                    {form.method==='aprazo' && form.salePrice && (
                      <InstallmentForm salePrice={form.salePrice} onChange={setInstallData}/>
                    )}

                    {history.length > 0 && (
                      <div className="mt-5">
                        <p className="text-xs font-semibold text-slate-500 mb-2">Histórico de Vendas</p>
                        <div className="space-y-2">
                          {history.map(sale => (
                            <div key={sale.id} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 overflow-hidden">
                              <div className="flex items-center justify-between px-3 py-2 text-xs gap-2">
                                <span className="text-slate-500 flex-shrink-0">{sale.date}</span>
                                <span className="text-slate-700 dark:text-slate-300">{sale.qty}x {fmt(sale.price||sale.salePrice||0)}</span>
                                <span className={`px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${sale.method==='avista'?'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400':'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'}`}>
                                  {sale.method==='avista'?'À Vista':'A Prazo'}
                                </span>
                                <span className={`font-semibold flex-shrink-0 ${sale.profit>=0?'text-emerald-600 dark:text-emerald-400':'text-red-500'}`}>{fmt(sale.profit)}</span>
                                <button onClick={() => deleteSale(product.id,sale.id)} className="text-slate-400 hover:text-red-500 transition-colors ml-1 flex-shrink-0"><X className="w-3 h-3"/></button>
                              </div>

                              {sale.method==='aprazo' && sale.parcelas && sale.parcelas.length>0 && (
                                <div className="border-t border-slate-100 dark:border-slate-800 px-3 py-2 space-y-1">
                                  <div className="flex items-center gap-2 text-xs bg-emerald-50 dark:bg-emerald-900/10 rounded-lg px-2 py-1.5">
                                    <span className="text-emerald-500 font-bold">✓</span>
                                    <span className="text-slate-500 w-16 flex-shrink-0">Entrada</span>
                                    <span className="font-semibold text-emerald-600 dark:text-emerald-400">{fmt(sale.entrada||0)}</span>
                                    <span className="ml-auto text-emerald-500 text-xs">recebido</span>
                                  </div>
                                  {sale.parcelas.map((parc, idx) => (
                                    <div key={parc.id} className={`flex items-center gap-2 text-xs rounded-lg px-2 py-1.5 ${parc.pago?'bg-emerald-50 dark:bg-emerald-900/10':'bg-slate-50 dark:bg-slate-800/50'}`}>
                                      <button onClick={() => toggleParcela(product.id,sale.id,parc.id)}
                                        className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${parc.pago?'border-emerald-500 bg-emerald-500':'border-slate-300 dark:border-slate-600 hover:border-emerald-400'}`}>
                                        {parc.pago && <span className="text-white text-[8px]">✓</span>}
                                      </button>
                                      <span className="text-slate-400 w-14 flex-shrink-0">{idx+1}ª parcela</span>
                                      <span className="text-slate-500">{new Date(parc.data+'T00:00:00').toLocaleDateString('pt-BR')}</span>
                                      <span className="font-medium text-slate-700 dark:text-slate-300 ml-auto">{fmt(parc.valor)}</span>
                                      <span className={`flex-shrink-0 w-16 text-right ${parc.pago?'text-emerald-500':'text-slate-400'}`}>{parc.pago?'Pago ✓':'Pendente'}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Histórico de Remessas */}
                    {((remessas[product.id]||[]).length > 0 || d.cost_price) && (
                      <div className="mt-4 border-t border-slate-200 dark:border-slate-700 pt-3">
                        <p className="text-xs font-semibold text-slate-500 mb-2">📦 Remessas</p>
                        <div className="space-y-1">
                          {(remessas[product.id]||[]).map((rm, i) => (
                            <div key={rm.id} className={`flex flex-wrap items-center gap-2 text-xs px-3 py-2 rounded-lg ${rm.ativa ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800' : 'bg-slate-50 dark:bg-slate-800/40'}`}>
                              <span className={`font-semibold flex-shrink-0 ${rm.ativa?'text-emerald-600 dark:text-emerald-400':'text-slate-400'}`}>{rm.ativa ? '● Ativa' : `Remessa ${(remessas[product.id]||[]).length - i}`}</span>
                              <span className="text-slate-500">{new Date(rm.data+'T12:00:00').toLocaleDateString('pt-BR')}</span>
                              <span className="text-slate-600 dark:text-slate-300">Custo: <strong>{fmt(rm.custo)}</strong></span>
                              <span className="text-slate-600 dark:text-slate-300">Venda: <strong>{fmt(rm.preco)}</strong></span>
                              <span className="text-slate-600 dark:text-slate-300">Qtd: <strong>{rm.qtd}</strong></span>
                              {rm.obs && <span className="text-slate-400 truncate">{rm.obs}</span>}
                              <div className="ml-auto flex items-center gap-1 flex-shrink-0">
                                <button onClick={() => { navigator.clipboard.writeText(rm.id); toast.success('ID copiado!'); }}
                                  className="text-[10px] bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded font-mono hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors"
                                  title="Copiar ID">ID: {rm.id.slice(0,8)}… 📋
                                </button>
                                <button onClick={async () => {
                                  if (!window.confirm('Excluir esta remessa?')) return;
                                  try {
                                    await axios.delete(`${API}/remessas/${product.id}/${rm.id}`, authHdr);
                                    setRemessas(prev => ({ ...prev, [product.id]: (prev[product.id]||[]).filter(r=>r.id!==rm.id) }));
                                    toast.success('Remessa removida!');
                                  } catch { toast.error('Erro ao remover'); }
                                }} className="text-red-400 hover:text-red-600 transition-colors p-0.5" title="Excluir">
                                  <X className="w-3 h-3"/>
                                </button>
                              </div>
                            </div>
                          ))}
                          {/* Remessa inicial — só mostra se não há remessas cadastradas */}
                          {d.cost_price && (remessas[product.id]||[]).length === 0 && (
                            <div className="flex flex-wrap items-center gap-2 text-xs px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/40 opacity-70">
                              <span className="text-slate-400 font-semibold">Remessa inicial</span>
                              <span className="text-slate-600 dark:text-slate-300">Custo: <strong>{fmt(d.cost_price)}</strong></span>
                              <span className="text-slate-600 dark:text-slate-300">Venda: <strong>{fmt(product.price)}</strong></span>
                              <span className="text-xs text-slate-400 italic">custo original do produto</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal Nova Remessa */}
      {remessaModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={e => { if(e.target===e.currentTarget) setRemessaModal(null); }}>
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-slate-900 dark:text-white text-lg">📦 Nova Remessa</h3>
              <button onClick={() => setRemessaModal(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5"/></button>
            </div>
            <p className="text-xs text-slate-500">Registre uma nova entrada de estoque. O custo e preço anteriores serão preservados no histórico.</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-slate-500 mb-1 block">ID da Remessa <span className="text-slate-400">(ex: remessa1, ali-jun26 — vazio = automático)</span></label>
                <input value={remessaForm.id} onChange={e=>setRemessaForm(f=>({...f,id:e.target.value.replace(/\s/g,'')}))} placeholder="Ex: remessa1, ali-maio26..."
                  className="w-full text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white font-mono"/>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Custo Unitário (R$) *</label>
                <input type="number" step="0.01" value={remessaForm.custo} onChange={e=>setRemessaForm(f=>({...f,custo:e.target.value}))} placeholder="0.00"
                  className="w-full text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white"/>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Preço de Venda (R$) *</label>
                <input type="number" step="0.01" value={remessaForm.preco} onChange={e=>setRemessaForm(f=>({...f,preco:e.target.value}))} placeholder="0.00"
                  className="w-full text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white"/>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Quantidade</label>
                <input type="number" min="1" value={remessaForm.qtd} onChange={e=>setRemessaForm(f=>({...f,qtd:e.target.value}))} placeholder="1"
                  className="w-full text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white"/>
              </div>
              <div>
                <label className="text-xs text-slate-500 mb-1 block">Data da Compra</label>
                <input type="date" value={remessaForm.data} onChange={e=>setRemessaForm(f=>({...f,data:e.target.value}))}
                  className="w-full text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white"/>
              </div>
              <div className="col-span-2">
                <label className="text-xs text-slate-500 mb-1 block">Observação</label>
                <input value={remessaForm.obs} onChange={e=>setRemessaForm(f=>({...f,obs:e.target.value}))} placeholder="Ex: Pedido AliExpress #123"
                  className="w-full text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white"/>
              </div>
            </div>
            {remessaForm.custo && remessaForm.preco && (
              <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg px-3 py-2 text-xs text-emerald-700 dark:text-emerald-400">
                Lucro por unidade: <strong>{fmt(parseFloat(remessaForm.preco) - parseFloat(remessaForm.custo))}</strong>
                {remessaForm.qtd > 1 && <span> · Total potencial: <strong>{fmt((parseFloat(remessaForm.preco) - parseFloat(remessaForm.custo)) * parseInt(remessaForm.qtd))}</strong></span>}
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button onClick={() => setRemessaModal(null)} className="flex-1 text-sm border border-slate-300 dark:border-slate-600 rounded-lg py-2 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">Cancelar</button>
              <button onClick={saveRemessa} className="flex-1 text-sm bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg py-2 font-medium transition-colors">✅ Salvar Remessa</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const PEDIDOS_KEY = 'fastfix_pedidos';
const CONTABIL_KEY = 'fastfix_contabil';

function loadLS(key, def) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : def; } catch { return def; }
}
function saveLS(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

function PedidosTab({ salesData, products, auth }) {
  const emptyForm = { nome:'', qtd:'1', custoPago:'', precoVenda:'', status:'aguardando', data: new Date().toISOString().split('T')[0], obs:'', product_id:'', remessa_id:'' };
  const [pedidos, setPedidos] = useState([]);
  const [loadingPed, setLoadingPed] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPedidoId, setEditingPedidoId] = useState(null);
  const [showPlanilha, setShowPlanilha] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [extraRows, setExtraRowsState] = useState([]);
  const [editingRow, setEditingRow] = useState(null);
  const authHdr = auth ? makeAuthHeader(auth.username, auth.password) : {};

  useEffect(() => {
    const loadPed = (t = 1) => {
      axios.get(`${API}/pedidos`, authHdr)
        .then(r => { if (Array.isArray(r.data)) setPedidos(r.data); })
        .catch(() => { if (t < 3) setTimeout(() => loadPed(t + 1), 3000); })
        .finally(() => setLoadingPed(false));
    };
    const loadExtra = (t = 1) => {
      axios.get(`${API}/extra-rows`, authHdr)
        .then(r => { if (Array.isArray(r.data)) setExtraRowsState(r.data); })
        .catch(() => { if (t < 3) setTimeout(() => loadExtra(t + 1), 3000); });
    };
    loadPed();
    loadExtra();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setExtraRows = async (updater) => {
    setExtraRowsState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      axios.post(`${API}/extra-rows`, { items: next }, authHdr).catch(() => {});
      return next;
    });
  };

  const openNewPedido = () => { setForm(emptyForm); setEditingPedidoId(null); setShowForm(true); };
  const openEditPedido = (p) => { setForm({ nome:p.nome, qtd:String(p.qtd), custoPago:String(p.custoPago), precoVenda:String(p.precoVenda), status:p.status, data:p.data, obs:p.obs||'' }); setEditingPedidoId(p.id); setShowForm(true); };

  const savePedido = async () => {
    if (!form.nome || !form.custoPago) return;
    const p = { ...form, qtd: parseInt(form.qtd)||1, custoPago: parseFloat(form.custoPago)||0, precoVenda: parseFloat(form.precoVenda)||0 };
    try {
      if (editingPedidoId) {
        await axios.put(`${API}/pedidos/${editingPedidoId}`, p, authHdr);
        setPedidos(prev => prev.map(x => x.id === editingPedidoId ? { ...x, ...p } : x));
      } else {
        const r = await axios.post(`${API}/pedidos`, p, authHdr);
        setPedidos(prev => [{ ...p, id: r.data.id }, ...prev]);
      }
      toast.success('Pedido salvo!');
    } catch { toast.error('Erro ao salvar pedido'); }
    setForm(emptyForm); setEditingPedidoId(null); setShowForm(false);
  };

  const removePedido = async (id) => {
    try {
      await axios.delete(`${API}/pedidos/${id}`, authHdr);
      setPedidos(prev => prev.filter(p => p.id !== id));
    } catch { toast.error('Erro ao remover pedido'); }
  };

  const updateStatus = async (id, status) => {
    try {
      const r = await axios.patch(`${API}/pedidos/${id}/status`, { status }, authHdr);
      setPedidos(prev => prev.map(p => p.id===id ? {...p, status} : p));
      // Se chegou e tem produto vinculado, atualiza localmente
      if (status === 'chegou' && r.data?.product_updated) {
        const { product_id, custo, preco } = r.data;
        // Dispara evento para SalesTab atualizar produto
        window.dispatchEvent(new CustomEvent('fastfix:produtoAtualizado', {
          detail: { product_id, custo, preco }
        }));
        toast.success('Pedido chegou! Produto atualizado em estoque. ✅');
      }
    } catch { toast.error('Erro ao atualizar status'); }
  };

  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [filtroDias, setFiltroDias] = useState('todos');

  const pedidosFiltrados = useMemo(() => {
    const agora = new Date();
    return pedidos.filter(p => {
      if (filtroStatus !== 'todos' && p.status !== filtroStatus) return false;
      if (filtroDias !== 'todos') {
        const dias = { '7':7, '15':15, '30':30, '90':90 }[filtroDias];
        const data = new Date(p.data + 'T12:00:00');
        if ((agora - data) / 86400000 > dias) return false;
      }
      return true;
    });
  }, [pedidos, filtroStatus, filtroDias]);

  const pedidosOrdenados = useMemo(() => {
    const aguardando = pedidosFiltrados.filter(p => p.status === 'aguardando');
    const transito   = pedidosFiltrados.filter(p => p.status === 'transito');
    const chegou     = pedidosFiltrados.filter(p => p.status === 'chegou');
    return { aguardando, transito, chegou };
  }, [pedidosFiltrados]);

  const totPedidos = useMemo(() => pedidos.reduce((acc, p) => ({
    investido: acc.investido + p.custoPago * p.qtd,
    esperado: acc.esperado + p.precoVenda * p.qtd,
    lucroEsperado: acc.lucroEsperado + (p.precoVenda - p.custoPago) * p.qtd,
    reinvestido: acc.reinvestido + p.custoPago * p.qtd,
  }), { investido: 0, esperado: 0, lucroEsperado: 0, reinvestido: 0 }), [pedidos]);

  // Planilha de vendas (backend) + linhas extras manuais
  const backendSales = useMemo(() => {
    const rows = [];
    for (const prod of products) {
      const hist = (salesData[prod.id]||{}).history || [];
      for (const s of hist) {
        rows.push({ _src:'backend', _pid:prod.id, _sid:s.id, produto: prod.name, categoria: prod.category, data: s.date, qty: parseInt(s.qty)||1, price: parseFloat(s.price||s.salePrice)||0, method: s.method, profit: parseFloat(s.profit)||0, entrada: parseFloat(s.entrada)||0, parcelas: s.parcelas||[] });
      }
    }
    return rows.sort((a,b) => {
      const parse = d => { const [dd,mm,yy]=d.split('/'); return new Date(`${yy}-${mm}-${dd}`); };
      return parse(b.data) - parse(a.data);
    });
  }, [salesData, products]);

  const allSales = useMemo(() => {
    const extras = extraRows.map(r => ({ ...r, _src:'extra' }));
    return [...backendSales, ...extras].sort((a,b) => {
      const parse = d => { try { const [dd,mm,yy]=d.split('/'); return new Date(`${yy}-${mm}-${dd}`); } catch { return new Date(0); } };
      return parse(b.data) - parse(a.data);
    });
  }, [backendSales, extraRows]);

  const emptyRowForm = { produto:'', categoria:'', data: new Date().toLocaleDateString('pt-BR'), qty:'1', price:'', method:'avista', profit:'', entrada:'0', obs:'' };

  const startEditRow = (idx) => {
    const r = allSales[idx];
    if (r._src === 'backend') {
      // Para linhas do sistema, abre em modo de edição como overlay (salva como extra sobrescrita)
      setEditingRow({ type:'backend', backendRef: { pid: r._pid, sid: r._sid }, data: { produto:r.produto, categoria:r.categoria||'', data:r.data, qty:String(r.qty), price:String(r.price), method:r.method, profit:String(r.profit), entrada:String(r.entrada), obs:r.obs||'' } });
      return;
    }
    const extraIdx = extraRows.findIndex(e => e.produto===r.produto && e.data===r.data && e.price===r.price);
    setEditingRow({ type:'edit', extraIdx, data: { produto:r.produto, categoria:r.categoria||'', data:r.data, qty:String(r.qty), price:String(r.price), method:r.method, profit:String(r.profit), entrada:String(r.entrada), obs:r.obs||'' } });
  };

  const saveRow = async () => {
    if (!editingRow) return;
    const d = editingRow.data;
    const row = { produto:d.produto, categoria:d.categoria, data:d.data, qty:parseInt(d.qty)||1, price:parseFloat(d.price)||0, method:d.method, profit:parseFloat(d.profit)||0, entrada:parseFloat(d.entrada)||0, parcelas:[], obs:d.obs };
    if (editingRow.type === 'new') {
      setExtraRows(prev => [row, ...prev]);
    } else if (editingRow.type === 'backend') {
      const { pid, sid } = editingRow.backendRef;
      try {
        const hdr = auth ? makeAuthHeader(auth.username, auth.password) : {};
        await axios.patch(`${API}/sales/${pid}/update`, {
          sale_id: sid, date: d.data, price: parseFloat(d.price)||0,
          profit: parseFloat(d.profit)||0, qty: parseInt(d.qty)||1,
          method: d.method, entrada: parseFloat(d.entrada)||0,
        }, hdr);
        toast.success('Venda atualizada! Recarregue para refletir nos cálculos.');
      } catch { toast.error('Erro ao atualizar venda'); }
    } else {
      setExtraRows(prev => prev.map((e,i) => i===editingRow.extraIdx ? row : e));
    }
    setEditingRow(null);
  };

  const deleteRow = (idx) => {
    const r = allSales[idx];
    if (r._src === 'backend') {
      if (window.confirm(`Apagar a venda "${r.produto}" do sistema?

Essa ação é permanente e irá remover do banco de dados.`)) {
        // Trigger delete via custom event (handled by SalesTabWithData wrapper)
        window.dispatchEvent(new CustomEvent('fastfix:deleteSale', { detail: { pid: r._pid, sid: r._sid } }));
      }
      return;
    }
    const extraIdx = extraRows.findIndex(e => e.produto===r.produto && e.data===r.data && e.price===r.price);
    if (extraIdx >= 0) setExtraRows(prev => prev.filter((_,i) => i!==extraIdx));
  };

  const statusInfo = {
    aguardando: { label:'Aguardando', color:'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon:<Clock className="w-3 h-3"/> },
    transito:   { label:'Em Trânsito', color:'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon:<Truck className="w-3 h-3"/> },
    chegou:     { label:'Chegou', color:'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon:<CheckCircle2 className="w-3 h-3"/> },
  };

  const inputCls = "text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 rounded px-2 py-1 text-slate-900 dark:text-white w-full";
  if (loadingPed) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-blue-600"/></div>;

  return (
    <div className="space-y-6">
      {/* Cards resumo — agora com 4 cards incluindo Reinvestido */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-xl border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 p-4">
          <div className="flex items-center gap-2 mb-1 text-red-600 dark:text-red-400 opacity-70"><ArrowDownCircle className="w-4 h-4"/><span className="text-xs font-medium">Total Investido</span></div>
          <p className="text-xl font-bold text-red-600 dark:text-red-400">{fmt(totPedidos.investido)}</p>
          <p className="text-xs text-red-400 mt-1">{pedidos.length} pedido(s)</p>
        </div>
        <div className="rounded-xl border bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 p-4">
          <div className="flex items-center gap-2 mb-1 text-orange-600 dark:text-orange-400 opacity-70"><PiggyBank className="w-4 h-4"/><span className="text-xs font-medium">Reinvestido</span></div>
          <p className="text-xl font-bold text-orange-600 dark:text-orange-400">{fmt(totPedidos.reinvestido)}</p>
          <p className="text-xs text-orange-400 mt-1">custo total dos pedidos</p>
        </div>
        <div className="rounded-xl border bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 p-4">
          <div className="flex items-center gap-2 mb-1 text-blue-600 dark:text-blue-400 opacity-70"><ArrowUpCircle className="w-4 h-4"/><span className="text-xs font-medium">Receita Esperada</span></div>
          <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{fmt(totPedidos.esperado)}</p>
        </div>
        <div className="rounded-xl border bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 p-4">
          <div className="flex items-center gap-2 mb-1 text-emerald-600 dark:text-emerald-400 opacity-70"><TrendingUp className="w-4 h-4"/><span className="text-xs font-medium">Lucro Esperado</span></div>
          <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{fmt(totPedidos.lucroEsperado)}</p>
        </div>
      </div>

      {/* Header pedidos + filtros */}
      <div className="space-y-3">
        <div className="flex justify-between items-center">
          <h2 className="font-semibold text-slate-900 dark:text-white text-lg flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-500"/>
            Pedidos ({pedidosFiltrados.length}{pedidosFiltrados.length !== pedidos.length ? ` de ${pedidos.length}` : ''})
          </h2>
          <button onClick={openNewPedido} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-2 rounded-lg transition-colors font-medium">
            <Plus className="w-4 h-4"/> Novo Pedido
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {/* Filtro status */}
          {[
            {id:'todos',      label:'Todos'},
            {id:'aguardando', label:'⏳ Aguardando'},
            {id:'transito',   label:'🚚 Trânsito'},
            {id:'chegou',     label:'✅ Chegou'},
          ].map(f => (
            <button key={f.id} onClick={() => setFiltroStatus(f.id)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${filtroStatus===f.id ? 'bg-blue-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}>
              {f.label}
            </button>
          ))}
          <div className="w-px bg-slate-300 dark:bg-slate-700 mx-1"/>
          {/* Filtro datas */}
          {[
            {id:'todos', label:'Todos'},
            {id:'7',     label:'7 dias'},
            {id:'15',    label:'15 dias'},
            {id:'30',    label:'30 dias'},
            {id:'90',    label:'3 meses'},
          ].map(f => (
            <button key={f.id} onClick={() => setFiltroDias(f.id)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${filtroDias===f.id ? 'bg-purple-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Lista de pedidos — ordenada: Aguardando → Trânsito → Chegou */}
      {pedidosFiltrados.length === 0 && (
        <div className="text-center py-12 text-slate-400">
          <Truck className="w-10 h-10 mx-auto mb-3 opacity-30"/>
          <p className="text-sm">Nenhum pedido encontrado</p>
        </div>
      )}

      {[
        { key:'aguardando', titulo:'⏳ Aguardando', lista: pedidosOrdenados.aguardando },
        { key:'transito',   titulo:'🚚 Em Trânsito', lista: pedidosOrdenados.transito },
        { key:'chegou',     titulo:'✅ Chegou',      lista: pedidosOrdenados.chegou, opaco: true },
      ].map(({ key, titulo, lista, opaco }) => lista.length > 0 && (
        <div key={key} className="space-y-2">
          {(filtroStatus === 'todos') && (
            <div className={`flex items-center gap-2 ${opaco ? 'opacity-50' : ''}`}>
              <span className="text-xs font-semibold text-slate-500">{titulo}</span>
              <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700"/>
              <span className="text-xs text-slate-400">{lista.length}</span>
            </div>
          )}
          <div className={`space-y-2 ${opaco ? 'opacity-60' : ''}`}>
            {lista.map(p => {
              const si = statusInfo[p.status] || statusInfo.aguardando;
              const lucroTotal = (p.precoVenda - p.custoPago) * p.qtd;
              return (
                <div key={p.id} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <p className="font-semibold text-slate-900 dark:text-white text-sm">{p.nome}</p>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${si.color}`}>
                          {si.icon} {si.label}
                        </span>
                        {p.product_id && <span className="text-xs text-emerald-500">🔗 vinculado</span>}
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                        <div><span className="text-slate-400">Qtd: </span><span className="font-medium text-slate-700 dark:text-slate-300">{p.qtd}x</span></div>
                        <div><span className="text-slate-400">Paguei: </span><span className="font-medium text-red-600 dark:text-red-400">{fmt(p.custoPago)}/un</span></div>
                        <div><span className="text-slate-400">Vendo: </span><span className="font-medium text-blue-600 dark:text-blue-400">{fmt(p.precoVenda)}/un</span></div>
                        <div><span className="text-slate-400">Lucro: </span><span className={`font-semibold ${lucroTotal>=0?'text-emerald-600 dark:text-emerald-400':'text-red-500'}`}>{fmt(lucroTotal)}</span></div>
                      </div>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <span className="text-xs text-slate-400">📅 {new Date(p.data+'T12:00:00').toLocaleDateString('pt-BR')}</span>
                        <span className="text-xs text-slate-400">Reinvestido: <strong className="text-orange-500">{fmt(p.custoPago*p.qtd)}</strong></span>
                        {p.obs && <span className="text-xs text-slate-400 italic">"{p.obs}"</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <select value={p.status} onChange={e => updateStatus(p.id, e.target.value)}
                        className="text-xs bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1 py-1 text-slate-700 dark:text-slate-300 max-w-[100px]">
                        <option value="aguardando">⏳ Aguardando</option>
                        <option value="transito">🚚 Trânsito</option>
                        <option value="chegou">✅ Chegou</option>
                      </select>
                      {p.status === 'chegou' && p.product_id && (
                        <button onClick={async () => {
                          try {
                            const r = await axios.post(`${API}/pedidos/${p.id}/aplicar-chegou`, {}, authHdr);
                            if (r.data?.product_updated) {
                              window.dispatchEvent(new CustomEvent('fastfix:produtoAtualizado', { detail: r.data }));
                              toast.success('Produto atualizado em estoque! ✅');
                            }
                          } catch { toast.error('Erro ao aplicar'); }
                        }} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded transition-colors">
                          🔄 Aplicar
                        </button>
                      )}
                      <button onClick={() => openEditPedido(p)} className="text-slate-400 hover:text-blue-500 transition-colors"><Pencil className="w-4 h-4"/></button>
                      <button onClick={() => removePedido(p.id)} className="text-slate-400 hover:text-red-500 transition-colors"><X className="w-4 h-4"/></button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Formulário pedido (novo ou edição) */}
      {showForm && (
        <div className="bg-white dark:bg-slate-900 border border-blue-200 dark:border-blue-800 rounded-xl p-5 space-y-4">
          <p className="font-semibold text-slate-800 dark:text-white text-sm">{editingPedidoId ? '✏️ Editar Pedido' : 'Registrar Novo Pedido'}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <p className="text-xs text-slate-500 mb-1">Produto Vinculado <span className="text-slate-400">(atualiza estoque ao chegar)</span></p>
              <select value={form.product_id} onChange={e=>setForm(f=>({...f,product_id:e.target.value}))}
                className="w-full text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white">
                <option value="">— Sem vínculo —</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Nome / descrição *</p>
              <input value={form.nome} onChange={e=>setForm(f=>({...f,nome:e.target.value}))} placeholder="Ex: Kit Carregadores Essager 33w x10"
                className="w-full text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white"/>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Quantidade *</p>
              <input type="number" min="1" value={form.qtd} onChange={e=>setForm(f=>({...f,qtd:e.target.value}))}
                className="w-full text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white"/>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Quanto paguei por unidade (R$) *</p>
              <input type="number" step="0.01" value={form.custoPago} onChange={e=>setForm(f=>({...f,custoPago:e.target.value}))} placeholder="0.00"
                className="w-full text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white"/>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Quanto vou vender por unidade (R$)</p>
              <input type="number" step="0.01" value={form.precoVenda} onChange={e=>setForm(f=>({...f,precoVenda:e.target.value}))} placeholder="0.00"
                className="w-full text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white"/>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Data do Pedido</p>
              <input type="date" value={form.data} onChange={e=>setForm(f=>({...f,data:e.target.value}))}
                className="w-full text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white"/>
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Status</p>
              <select value={form.status} onChange={e=>setForm(f=>({...f,status:e.target.value}))}
                className="w-full text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white">
                <option value="aguardando">⏳ Aguardando</option>
                <option value="transito">🚚 Em Trânsito</option>
                <option value="chegou">✅ Chegou</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <p className="text-xs text-slate-500 mb-1">Observações</p>
              <input value={form.obs} onChange={e=>setForm(f=>({...f,obs:e.target.value}))} placeholder="Ex: Shopee, previsão 5 dias..."
                className="w-full text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white"/>
            </div>
            <div className="sm:col-span-2">
              <p className="text-xs text-slate-500 mb-1">ID da Remessa <span className="text-slate-400">(copie da aba Vendas & Lucros → produto → Remessas)</span></p>
              <div className="flex gap-2">
                <input value={form.remessa_id} onChange={e=>setForm(f=>({...f,remessa_id:e.target.value}))} placeholder="Cole o ID da remessa aqui..."
                  className="flex-1 text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white font-mono"/>
                {form.remessa_id && <span className="text-xs text-emerald-500 self-center">✓ vinculado</span>}
              </div>
            </div>
          </div>
          {form.custoPago && form.precoVenda && (
            <div className="text-xs bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg px-3 py-2 text-emerald-700 dark:text-emerald-400">
              💰 Lucro esperado: <strong>{fmt((parseFloat(form.precoVenda)-parseFloat(form.custoPago))*(parseInt(form.qtd)||1))}</strong> &nbsp;|&nbsp;
              Reinvestido: <strong>{fmt(parseFloat(form.custoPago)*(parseInt(form.qtd)||1))}</strong>
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={savePedido} className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-1">
              <Save className="w-4 h-4"/> {editingPedidoId ? 'Atualizar' : 'Salvar Pedido'}
            </button>
            <button onClick={() => { setShowForm(false); setEditingPedidoId(null); setForm(emptyForm); }} className="text-slate-500 hover:text-slate-700 text-sm px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 transition-colors">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Planilha de vendas com edição inline */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
        <button onClick={() => setShowPlanilha(!showPlanilha)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
          <div className="flex items-center gap-2">
            <Table2 className="w-5 h-5 text-purple-500"/>
            <span className="font-semibold text-slate-900 dark:text-white">Planilha de Vendas</span>
            <span className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 px-2 py-0.5 rounded-full">{allSales.length} registros</span>
          </div>
          {showPlanilha ? <ChevronUp className="w-4 h-4 text-slate-400"/> : <ChevronDown className="w-4 h-4 text-slate-400"/>}
        </button>
        {showPlanilha && (
          <div className="border-t border-slate-100 dark:border-slate-800">
            {/* Linha de nova entrada manual */}
            <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <span className="text-xs text-slate-500">Linhas do sistema podem ser editadas. Use "+ Linha" para adicionar entradas extras.</span>
              <button onClick={() => setEditingRow({ type:'new', data: {...emptyRowForm} })}
                className="flex items-center gap-1 text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-lg transition-colors font-medium">
                <Plus className="w-3 h-3"/> Linha
              </button>
            </div>

            {/* Formulário de nova linha manual */}
            {editingRow && (
              <div className="px-4 py-3 bg-purple-50 dark:bg-purple-900/10 border-b border-purple-200 dark:border-purple-800 space-y-2">
                <p className="text-xs font-semibold text-purple-700 dark:text-purple-400">{editingRow.type==='new' ? '➕ Nova linha manual' : '✏️ Editar linha'}</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div><p className="text-xs text-slate-500 mb-1">Produto</p><input value={editingRow.data.produto} onChange={e=>setEditingRow(r=>({...r,data:{...r.data,produto:e.target.value}}))} className={inputCls} placeholder="Nome"/></div>
                  <div><p className="text-xs text-slate-500 mb-1">Categoria</p><input value={editingRow.data.categoria} onChange={e=>setEditingRow(r=>({...r,data:{...r.data,categoria:e.target.value}}))} className={inputCls} placeholder="Cat."/></div>
                  <div><p className="text-xs text-slate-500 mb-1">Data (DD/MM/AAAA)</p><input value={editingRow.data.data} onChange={e=>setEditingRow(r=>({...r,data:{...r.data,data:e.target.value}}))} className={inputCls} placeholder="DD/MM/AAAA"/></div>
                  <div><p className="text-xs text-slate-500 mb-1">Qtd</p><input type="number" value={editingRow.data.qty} onChange={e=>setEditingRow(r=>({...r,data:{...r.data,qty:e.target.value}}))} className={inputCls}/></div>
                  <div><p className="text-xs text-slate-500 mb-1">Preço Unit.</p><input type="number" step="0.01" value={editingRow.data.price} onChange={e=>setEditingRow(r=>({...r,data:{...r.data,price:e.target.value}}))} className={inputCls} placeholder="0.00"/></div>
                  <div><p className="text-xs text-slate-500 mb-1">Método</p>
                    <select value={editingRow.data.method} onChange={e=>setEditingRow(r=>({...r,data:{...r.data,method:e.target.value}}))} className={inputCls}>
                      <option value="avista">À Vista</option><option value="aprazo">A Prazo</option>
                    </select>
                  </div>
                  <div><p className="text-xs text-slate-500 mb-1">Lucro</p><input type="number" step="0.01" value={editingRow.data.profit} onChange={e=>setEditingRow(r=>({...r,data:{...r.data,profit:e.target.value}}))} className={inputCls} placeholder="0.00"/></div>
                  <div><p className="text-xs text-slate-500 mb-1">Entrada</p><input type="number" step="0.01" value={editingRow.data.entrada} onChange={e=>setEditingRow(r=>({...r,data:{...r.data,entrada:e.target.value}}))} className={inputCls} placeholder="0.00"/></div>
                </div>
                <div className="flex gap-2 pt-1">
                  <button onClick={saveRow} className="flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg font-medium"><Save className="w-3 h-3"/> Salvar</button>
                  <button onClick={() => setEditingRow(null)} className="text-xs text-slate-500 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600">Cancelar</button>
                </div>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr>
                    {['Data','Produto','Categoria','Qtd','Preço Unit.','Método','Lucro','Entrada','Parcelas','Ações'].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-semibold text-slate-600 dark:text-slate-400 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {allSales.map((s, i) => {
                    const isBackend = s._src === 'backend';
                    return (
                      <tr key={i} className={`hover:bg-slate-50 dark:hover:bg-slate-800/50 ${!isBackend?'bg-purple-50/40 dark:bg-purple-900/5':''}`}>
                        <td className="px-3 py-2 whitespace-nowrap text-slate-500">{s.data}</td>
                        <td className="px-3 py-2 text-slate-900 dark:text-white font-medium max-w-[160px] truncate">{s.produto}</td>
                        <td className="px-3 py-2 text-slate-500 whitespace-nowrap">{s.categoria||'-'}</td>
                        <td className="px-3 py-2 text-center text-slate-700 dark:text-slate-300">{s.qty}</td>
                        <td className="px-3 py-2 whitespace-nowrap text-slate-700 dark:text-slate-300">{fmt(s.price)}</td>
                        <td className="px-3 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${s.method==='avista'?'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400':'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'}`}>
                            {s.method==='avista'?'À Vista':'A Prazo'}
                          </span>
                        </td>
                        <td className={`px-3 py-2 font-semibold whitespace-nowrap ${s.profit>=0?'text-emerald-600 dark:text-emerald-400':'text-red-500'}`}>{fmt(s.profit)}</td>
                        <td className="px-3 py-2 text-slate-500">{s.method==='aprazo'?fmt(s.entrada):'-'}</td>
                        <td className="px-3 py-2 text-slate-500">{s.parcelas&&s.parcelas.length > 0 ? `${s.parcelas.filter(p=>p.pago).length}/${s.parcelas.length} pagas` : '-'}</td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1">
                            <>
                              <button onClick={() => startEditRow(i)} className="text-blue-400 hover:text-blue-600 transition-colors" title="Editar"><Pencil className="w-3 h-3"/></button>
                              <button onClick={() => deleteRow(i)} className="text-red-400 hover:text-red-600 transition-colors" title="Apagar"><Trash2 className="w-3 h-3"/></button>
                            </>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-50 dark:bg-slate-800 border-t-2 border-slate-200 dark:border-slate-700">
                  <tr>
                    <td colSpan={3} className="px-3 py-2 font-semibold text-slate-600 dark:text-slate-400">TOTAL</td>
                    <td className="px-3 py-2 font-bold text-slate-900 dark:text-white">{allSales.reduce((s,r)=>s+(parseInt(r.qty)||0),0)}</td>
                    <td className="px-3 py-2 font-bold text-slate-900 dark:text-white">{fmt(allSales.reduce((s,r)=>s+(parseFloat(r.price)||0)*(parseInt(r.qty)||1),0))}</td>
                    <td/>
                    <td className="px-3 py-2 font-bold text-emerald-600 dark:text-emerald-400">{fmt(allSales.reduce((s,r)=>s+(parseFloat(r.profit)||0),0))}</td>
                    <td className="px-3 py-2 font-bold text-slate-900 dark:text-white">{fmt(allSales.reduce((s,r)=>s+(parseFloat(r.entrada)||0),0))}</td>
                    <td colSpan={2}/>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const TIPOS_MOV = [
  { id:'retirada_lucro', emoji:'💸', label:'Retirada do Lucro',          desc:'Retirar dinheiro do lucro acumulado' },
  { id:'retirada_caixa', emoji:'💰', label:'Retirada do Caixa',          desc:'Retirar dinheiro do caixa' },
  { id:'aporte',         emoji:'➕', label:'Aporte / Entrada',            desc:'Adicionar dinheiro ao caixa' },
  { id:'starlink',       emoji:'📡', label:'Aluguel Starlink',            desc:'Registro de aluguel de internet Starlink' },
  { id:'manutencao',     emoji:'🖥️', label:'Manutenção de Computador',   desc:'Serviço de manutenção de equipamentos' },
  { id:'servico_info',   emoji:'💻', label:'Serviços de Informática',     desc:'Outros serviços de TI/informática' },
  { id:'afiliado',       emoji:'🛒', label:'Venda de Afiliado',           desc:'Comissão de venda como afiliado' },
  { id:'servico_geral',  emoji:'🔧', label:'Serviço Geral',               desc:'Qualquer outro serviço prestado' },
];

const TIPO_INFO = {
  retirada_lucro: { label:'Retirada do Lucro',        color:'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',  isOut:true  },
  retirada_caixa: { label:'Retirada do Caixa',        color:'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',              isOut:true  },
  aporte:         { label:'Aporte / Entrada',          color:'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', isOut:false },
  starlink:       { label:'Aluguel Starlink',          color:'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',          isOut:false },
  manutencao:     { label:'Manutenção de Computador',  color:'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400',          isOut:false },
  servico_info:   { label:'Serviços de Informática',   color:'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',  isOut:false },
  afiliado:       { label:'Venda de Afiliado',         color:'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',  isOut:false },
  servico_geral:  { label:'Serviço Geral',             color:'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400',         isOut:false },
};

const RECEITA_TIPOS = ['aporte','starlink','manutencao','servico_info','afiliado','servico_geral'];

const EMPTY_FORM = { tipo:'retirada_lucro', valor:'', descricao:'', data: new Date().toISOString().split('T')[0], cliente:'', diaria:'', plataforma:'', dataReceb:'' };

function CamposDinamicos({ form, setForm, cls }) {
  const t = form.tipo;
  if (t === 'starlink') return (
    <>
      <div><p className="text-xs text-slate-500 mb-1">Nome da Pessoa *</p>
        <input value={form.cliente} onChange={e=>setForm(f=>({...f,cliente:e.target.value}))} placeholder="Ex: João Silva" className={cls}/></div>
      <div><p className="text-xs text-slate-500 mb-1">Diária (R$/dia)</p>
        <input type="number" step="0.01" value={form.diaria} onChange={e=>setForm(f=>({...f,diaria:e.target.value}))} placeholder="0.00" className={cls}/></div>
      <div><p className="text-xs text-slate-500 mb-1">Período / Observação</p>
        <input value={form.descricao} onChange={e=>setForm(f=>({...f,descricao:e.target.value}))} placeholder="Ex: 15 dias de maio" className={cls}/></div>
    </>
  );
  if (t === 'manutencao') return (
    <>
      <div><p className="text-xs text-slate-500 mb-1">Nome do Cliente *</p>
        <input value={form.cliente} onChange={e=>setForm(f=>({...f,cliente:e.target.value}))} placeholder="Ex: Maria Costa" className={cls}/></div>
      <div><p className="text-xs text-slate-500 mb-1">Descrição do Serviço</p>
        <input value={form.descricao} onChange={e=>setForm(f=>({...f,descricao:e.target.value}))} placeholder="Ex: Formatação + SSD" className={cls}/></div>
    </>
  );
  if (t === 'servico_info') return (
    <>
      <div><p className="text-xs text-slate-500 mb-1">Descrição do Serviço *</p>
        <input value={form.descricao} onChange={e=>setForm(f=>({...f,descricao:e.target.value}))} placeholder="Ex: Instalação de câmeras" className={cls}/></div>
      <div><p className="text-xs text-slate-500 mb-1">Cliente</p>
        <input value={form.cliente} onChange={e=>setForm(f=>({...f,cliente:e.target.value}))} placeholder="Opcional" className={cls}/></div>
    </>
  );
  if (t === 'afiliado') return (
    <>
      <div><p className="text-xs text-slate-500 mb-1">Plataforma *</p>
        <select value={form.plataforma} onChange={e=>setForm(f=>({...f,plataforma:e.target.value}))} className={cls}>
          <option value="">Selecione...</option>
          <option value="Mercado Livre">🛍️ Mercado Livre</option>
          <option value="Amazon">📦 Amazon</option>
          <option value="AliExpress">🌏 AliExpress</option>
          <option value="Shopee">🛒 Shopee</option>
          <option value="Magalu">🏪 Magalu</option>
        </select></div>
      <div><p className="text-xs text-slate-500 mb-1">Data de Recebimento</p>
        <input type="date" value={form.dataReceb} onChange={e=>setForm(f=>({...f,dataReceb:e.target.value}))} className={cls}/></div>
      <div><p className="text-xs text-slate-500 mb-1">Produto / Descrição</p>
        <input value={form.descricao} onChange={e=>setForm(f=>({...f,descricao:e.target.value}))} placeholder="Ex: Fone bluetooth" className={cls}/></div>
    </>
  );
  if (t === 'servico_geral') return (
    <>
      <div className="sm:col-span-2"><p className="text-xs text-slate-500 mb-1">Descrição *</p>
        <textarea value={form.descricao} onChange={e=>setForm(f=>({...f,descricao:e.target.value}))} placeholder="Descreva o serviço realizado..." rows={2} className={cls+" resize-none"}/></div>
      <div><p className="text-xs text-slate-500 mb-1">Cliente</p>
        <input value={form.cliente} onChange={e=>setForm(f=>({...f,cliente:e.target.value}))} placeholder="Opcional" className={cls}/></div>
    </>
  );
  // retirada_lucro, retirada_caixa, aporte
  return (
    <div><p className="text-xs text-slate-500 mb-1">Descrição</p>
      <input value={form.descricao} onChange={e=>setForm(f=>({...f,descricao:e.target.value}))} placeholder="Ex: Retirada para pagar fornecedor..." className={cls}/></div>
  );
}

function ContabilidadeTab({ caixaReal, lucroTotal, mesAtual, auth }) {
  const [movimentos, setMovimentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const authHdr = auth ? makeAuthHeader(auth.username, auth.password) : {};

  useEffect(() => {
    const load = (tentativa = 1) => {
      axios.get(`${API}/movimentos`, authHdr)
        .then(r => { if (Array.isArray(r.data)) setMovimentos(r.data); })
        .catch(() => {
          if (tentativa < 3) setTimeout(() => load(tentativa + 1), 3000);
          else toast.error('Erro ao carregar movimentos. Recarregue a página.');
        })
        .finally(() => setLoading(false));
    };
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addMov = async () => {
    if (!form.valor || parseFloat(form.valor) <= 0) return;
    const mv = { ...form, valor: parseFloat(form.valor) };
    try {
      const r = await axios.post(`${API}/movimentos`, mv, authHdr);
      setMovimentos(prev => [{ ...mv, id: r.data.id }, ...prev]);
      setForm(EMPTY_FORM);
      setShowForm(false);
      toast.success('Movimento salvo!');
    } catch { toast.error('Erro ao salvar movimento'); }
  };

  const removeMov = async (id) => {
    try {
      await axios.delete(`${API}/movimentos/${id}`, authHdr);
      setMovimentos(prev => prev.filter(m => m.id !== id));
    } catch { toast.error('Erro ao remover movimento'); }
  };

  const totals = useMemo(() => {
    const SERVICOS = ['starlink','manutencao','servico_info','afiliado','servico_geral'];

    let receitaServicos = 0;
    let totalAportes    = 0;
    let retLucro        = 0;
    let retCaixa        = 0;

    movimentos.forEach(m => {
      const v = m.valor;
      if (SERVICOS.includes(m.tipo))          receitaServicos += v;
      else if (m.tipo === 'aporte')           totalAportes    += v;
      else if (m.tipo === 'retirada_lucro')   retLucro        += v;
      else if (m.tipo === 'retirada_caixa')   retCaixa        += v;
    });

    // Lucro bruto = lucro vendas + serviços
    const lucroBrutoMes = lucroTotal + receitaServicos;

    // Caixa base = tudo que entrou
    const caixaBase     = caixaReal + receitaServicos + totalAportes;
    // Parte "custo" do caixa (não é lucro)
    const caixaSemLucro = Math.max(0, caixaBase - lucroBrutoMes);
    // Retirada de caixa: retira do custo primeiro; excesso afeta lucro
    const retCaixaDoCusto = Math.min(retCaixa, caixaSemLucro);
    const retCaixaDoLucro = Math.max(0, retCaixa - retCaixaDoCusto);

    // Lucro líquido = bruto - retiradas de lucro - parte da ret.caixa que afetou lucro
    const lucroLiquido = Math.max(0, lucroBrutoMes - retLucro - retCaixaDoLucro);

    // Caixa Real = lucro bruto + aportes (sem reinvestimento)
    const caixaReal_calc = lucroBrutoMes + totalAportes;

    // Caixa Líquido = entradas - saídas (matemática pura)
    const caixaLiquido = Math.max(0, caixaBase - retLucro - retCaixa);

    const porTipo = {};
    RECEITA_TIPOS.forEach(t => {
      porTipo[t] = movimentos.filter(m => m.tipo === t).reduce((s, m) => s + m.valor, 0);
    });

    return { lucroBrutoMes, lucroLiquido, retLucro, retCaixa, totalAportes, caixaReal: caixaReal_calc, caixaLiquido, porTipo };
  }, [movimentos, caixaReal, lucroTotal]);


  const { lucroBrutoMes, lucroLiquido, retLucro, retCaixa, totalAportes, caixaReal: caixaCalc, caixaLiquido, porTipo } = totals;
  const inpCls = "w-full text-sm bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-slate-900 dark:text-white";
  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-blue-600"/></div>;

  return (
    <div className="space-y-6">
      {/* Cards principais */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-xl border bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 p-4">
          <div className="flex items-center gap-2 mb-1 text-emerald-600 dark:text-emerald-400 opacity-70"><TrendingUp className="w-4 h-4"/><span className="text-xs font-medium">Lucro Bruto Mês</span></div>
          <p className="text-xl font-bold text-emerald-600 dark:text-emerald-400">{fmt(lucroBrutoMes)}</p>
          <p className="text-xs text-emerald-400 mt-1">Vendas + serviços · {mesAtual}</p>
        </div>
        <div className="rounded-xl border bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800 p-4">
          <div className="flex items-center gap-2 mb-1 text-purple-600 dark:text-purple-400 opacity-70"><ArrowUpCircle className="w-4 h-4"/><span className="text-xs font-medium">Lucro Líquido</span></div>
          <p className="text-xl font-bold text-purple-600 dark:text-purple-400">{fmt(lucroLiquido)}</p>
          {retLucro > 0 && <p className="text-xs text-purple-400 mt-1">Retirado: {fmt(retLucro)}</p>}
        </div>
        <div className="rounded-xl border bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800 p-4">
          <div className="flex items-center gap-2 mb-1 text-yellow-600 dark:text-yellow-400 opacity-70"><Wallet className="w-4 h-4"/><span className="text-xs font-medium">Caixa Real</span></div>
          <p className="text-xl font-bold text-yellow-600 dark:text-yellow-400">{fmt(caixaCalc)}</p>
          <p className="text-xs text-yellow-400 mt-1">Lucro + aportes + reinvest.</p>
        </div>
        <div className="rounded-xl border bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800 p-4">
          <div className="flex items-center gap-2 mb-1 text-orange-600 dark:text-orange-400 opacity-70"><DollarSign className="w-4 h-4"/><span className="text-xs font-medium">Caixa Líquido</span></div>
          <p className="text-xl font-bold text-orange-600 dark:text-orange-400">{fmt(caixaLiquido)}</p>
          <p className="text-xs text-orange-400 mt-1">Entradas − saídas</p>
        </div>
      </div>

      {/* Cards de receitas extras (só aparece se tiver valor) */}
      {[
        { id:'starlink',     emoji:'📡', label:'Starlink',      color:'blue'   },
        { id:'manutencao',   emoji:'🖥️', label:'Manutenção',   color:'cyan'   },
        { id:'servico_info', emoji:'💻', label:'Informática',   color:'indigo' },
        { id:'afiliado',     emoji:'🛒', label:'Afiliados',     color:'orange' },
        { id:'servico_geral',emoji:'🔧', label:'Serviços',      color:'teal'   },
        { id:'aporte',       emoji:'➕', label:'Aportes',       color:'emerald'},
      ].filter(c => porTipo[c.id] > 0).length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { id:'starlink',     emoji:'📡', label:'Starlink',    color:'blue'   },
            { id:'manutencao',   emoji:'🖥️', label:'Manutenção', color:'cyan'   },
            { id:'servico_info', emoji:'💻', label:'Informática', color:'indigo' },
            { id:'afiliado',     emoji:'🛒', label:'Afiliados',   color:'orange' },
            { id:'servico_geral',emoji:'🔧', label:'Serviços',    color:'teal'   },
            { id:'aporte',       emoji:'➕', label:'Aportes',     color:'emerald'},
          ].filter(c => porTipo[c.id] > 0).map(c => (
            <div key={c.id} className={`rounded-xl border bg-${c.color}-50 dark:bg-${c.color}-900/20 border-${c.color}-200 dark:border-${c.color}-800 p-3`}>
              <div className={`text-xs font-medium text-${c.color}-600 dark:text-${c.color}-400 opacity-70 mb-1`}>{c.emoji} {c.label}</div>
              <p className={`text-base font-bold text-${c.color}-600 dark:text-${c.color}-400`}>{fmt(porTipo[c.id])}</p>
            </div>
          ))}
        </div>
      )}

      {/* Botão */}
      <div className="flex justify-between items-center">
        <h2 className="font-semibold text-slate-900 dark:text-white text-lg flex items-center gap-2"><BookOpen className="w-5 h-5 text-purple-500"/>Movimentos ({movimentos.length})</h2>
        <button onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-sm px-4 py-2 rounded-lg transition-colors font-medium">
          <Plus className="w-4 h-4"/> Novo Movimento
        </button>
      </div>

      {/* Formulário */}
      {showForm && (
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 space-y-4">
          <p className="font-semibold text-slate-800 dark:text-white text-sm">Registrar Movimento</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Tipo */}
            <div className="sm:col-span-2">
              <p className="text-xs text-slate-500 mb-1">Tipo</p>
              <select value={form.tipo} onChange={e=>setForm(f=>({...EMPTY_FORM, tipo:e.target.value, data:f.data}))} className={inpCls}>
                {TIPOS_MOV.map(t=>(
                  <option key={t.id} value={t.id}>{t.emoji} {t.label}</option>
                ))}
              </select>
            </div>
            {/* Campos dinâmicos por tipo */}
            <CamposDinamicos form={form} setForm={setForm} cls={inpCls}/>
            {/* Valor */}
            <div>
              <p className="text-xs text-slate-500 mb-1">Valor (R$) *</p>
              <input type="number" step="0.01" value={form.valor} onChange={e=>setForm(f=>({...f,valor:e.target.value}))} placeholder="0.00" className={inpCls}/>
            </div>
            {/* Data */}
            <div>
              <p className="text-xs text-slate-500 mb-1">Data</p>
              <input type="date" value={form.data} onChange={e=>setForm(f=>({...f,data:e.target.value}))} className={inpCls}/>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={addMov} className="bg-purple-600 hover:bg-purple-700 text-white text-sm px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-1">
              <Save className="w-4 h-4"/> Salvar
            </button>
            <button onClick={() => { setShowForm(false); setForm(EMPTY_FORM); }} className="text-slate-500 text-sm px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 transition-colors">
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Lista */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
        {movimentos.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30"/>
            <p className="text-sm">Nenhum movimento registrado</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {movimentos.map(m => {
              const ti = TIPO_INFO[m.tipo] || TIPO_INFO.retirada_lucro;
              const emoji = TIPOS_MOV.find(t=>t.id===m.tipo)?.emoji || '📋';
              const sub = m.plataforma ? `${m.plataforma}${m.descricao?' · '+m.descricao:''}` :
                          m.cliente    ? `${m.cliente}${m.descricao?' · '+m.descricao:''}` :
                          m.descricao  || '';
              const dataExib = m.tipo === 'afiliado' && m.dataReceb ? m.dataReceb : m.data;
              return (
                <div key={m.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-lg ${ti.isOut?'bg-red-100 dark:bg-red-900/20':'bg-emerald-100 dark:bg-emerald-900/20'}`}>
                    {emoji}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ti.color}`}>{ti.label}</span>
                    </div>
                    {sub && <p className="text-xs text-slate-500 mt-0.5 truncate">{sub}</p>}
                    <p className="text-xs text-slate-400 mt-0.5">{new Date((dataExib||m.data)+'T12:00:00').toLocaleDateString('pt-BR')}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`font-bold text-sm ${ti.isOut?'text-red-600 dark:text-red-400':'text-emerald-600 dark:text-emerald-400'}`}>
                      {ti.isOut?'-':'+'} {fmt(m.valor)}
                    </p>
                  </div>
                  <button onClick={() => removeMov(m.id)} className="text-slate-400 hover:text-red-500 transition-colors ml-1 flex-shrink-0"><X className="w-4 h-4"/></button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


function SalesTabWithData({ products, setProducts, auth }) {
  const [salesData, setSalesData] = useState({});
  const [loading, setLoading] = useState(true);
  const authHdr = makeAuthHeader(auth.username, auth.password);

  const loadSales = () => {
    axios.get(`${API}/sales`, authHdr)
      .then(r => { const m={}; for(const d of r.data) m[d.product_id]=d; setSalesData(m); })
      .catch(()=>{})
      .finally(()=>setLoading(false));
  };

  useEffect(() => {
    loadSales();
    const handler = async (e) => {
      const { pid, sid } = e.detail;
      try {
        await axios.delete(`${API}/sales/${pid}/${sid}`, authHdr);
        setSalesData(prev => {
          const ex = prev[pid]||{};
          return { ...prev, [pid]: { ...ex, history: (ex.history||[]).filter(s=>s.id!==sid) } };
        });
        toast.success('Venda removida!');
      } catch { toast.error('Erro ao remover venda'); }
    };
    const handlerAtualizado = (e) => {
      const { product_id, custo, preco } = e.detail;
      setSalesData(prev => ({
        ...prev,
        [product_id]: { ...(prev[product_id]||{product_id}), cost_price: custo }
      }));
      setProducts(prev => prev.map(p => p.id === product_id ? { ...p, price: preco, in_stock: true } : p));
    };
    window.addEventListener('fastfix:deleteSale', handler);
    window.addEventListener('fastfix:produtoAtualizado', handlerAtualizado);
    return () => {
      window.removeEventListener('fastfix:deleteSale', handler);
      window.removeEventListener('fastfix:produtoAtualizado', handlerAtualizado);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-blue-600"/></div>;
  return <PedidosTab products={products} salesData={salesData} auth={auth}/>;
}

function ContabilidadeWrapper({ products, auth }) {
  const [salesData, setSalesData] = useState({});
  const [filtroPeriodo, setFiltroPeriodo] = useState('mes'); // mes | 30d | 90d | ano | tudo
  const authHdr = makeAuthHeader(auth.username, auth.password);

  useEffect(() => {
    axios.get(`${API}/sales`, authHdr)
      .then(r => { const m={}; for(const d of r.data) m[d.product_id]=d; setSalesData(m); })
      .catch(()=>{});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const now = new Date();
  const mesAtual = `${String(now.getMonth()+1).padStart(2,'0')}/${now.getFullYear()}`;

  const periodoLabel = {
    mes: `Mês ${mesAtual}`, '30d': 'Últimos 30 dias',
    '90d': 'Últimos 3 meses', ano: `Ano ${now.getFullYear()}`, tudo: 'Todo o período'
  };

  const dentroFiltro = (dateStr) => {
    if (!dateStr) return false;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return false;
    const [dd, mm, yy] = parts;
    const d = new Date(`${yy}-${mm}-${dd}T12:00:00`);
    if (filtroPeriodo === 'mes')  return `${mm}/${yy}` === mesAtual;
    if (filtroPeriodo === '30d')  return (now - d) / 86400000 <= 30;
    if (filtroPeriodo === '90d')  return (now - d) / 86400000 <= 90;
    if (filtroPeriodo === 'ano')  return yy === String(now.getFullYear());
    return true; // tudo
  };

  const totals = products.reduce((acc, p) => {
    const h = (salesData[p.id]||{}).history||[];
    const hFilt = h.filter(s => dentroFiltro(s.date));
    const avP = hFilt.filter(s=>s.method==='avista').reduce((s,h)=>s+(parseFloat(h.price||h.salePrice)||0)*(parseInt(h.qty)||1),0);
    const apR = hFilt.filter(s=>s.method==='aprazo').reduce((s,h)=>s+(parseFloat(h.entrada)||0)+(h.parcelas||[]).filter(p=>p.pago).reduce((ps,p)=>ps+parseFloat(p.valor||0),0),0);
    return {
      profitFilt: acc.profitFilt + hFilt.reduce((s,h)=>s+(parseFloat(h.profit)||0),0),
      caixaFilt:  acc.caixaFilt  + avP + apR,
    };
  }, { profitFilt: 0, caixaFilt: 0 });

  return (
    <div className="space-y-4">
      {/* Filtro de período */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs text-slate-500 font-medium">Período:</span>
        {[
          {id:'mes',  label:`Mês atual`},
          {id:'30d',  label:'30 dias'},
          {id:'90d',  label:'3 meses'},
          {id:'ano',  label:'Este ano'},
          {id:'tudo', label:'Tudo'},
        ].map(f => (
          <button key={f.id} onClick={() => setFiltroPeriodo(f.id)}
            className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${filtroPeriodo===f.id ? 'bg-purple-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}>
            {f.label}
          </button>
        ))}
        <span className="text-xs text-slate-400 ml-1">· {periodoLabel[filtroPeriodo]}</span>
      </div>
      <ContabilidadeTab
        caixaReal={totals.caixaFilt}
        lucroTotal={totals.profitFilt}
        mesAtual={periodoLabel[filtroPeriodo]}
        auth={auth}
      />
    </div>
  );
}

export default function AdminPage() {
  const [credentials, setCredentials] = useState(() => loadSession() || { username:'', password:'' });
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!loadSession());
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState(emptyProduct);
  const [filtroEstoque, setFiltroEstoque] = useState('todos'); // todos | estoque | esgotado
  const [activeTab, setActiveTab] = useState(() => {
    try { return sessionStorage.getItem('ff_active_tab') || 'products'; } catch { return 'products'; }
  });

  const handleSetTab = (tab) => {
    setActiveTab(tab);
    try { sessionStorage.setItem('ff_active_tab', tab); } catch {}
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (isAuthenticated) fetchProducts(); }, [isAuthenticated]);

  const getAuth = () => makeAuthHeader(credentials.username, credentials.password);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      await axios.post(`${API}/admin/login`, null, getAuth());
      saveSession(credentials);
      setIsAuthenticated(true);
      toast.success('Login realizado com sucesso!');
    } catch { toast.error('Credenciais inválidas'); }
    finally { setLoading(false); }
  };

  const handleLogout = () => {
    clearSession(); setIsAuthenticated(false);
    setCredentials({ username:'', password:'' }); setProducts([]);
    toast.info('Logout realizado');
  };

  const fetchProducts = async () => {
    try { setLoading(true); const r = await axios.get(`${API}/products`); setProducts(r.data); }
    catch { toast.error('Erro ao carregar produtos'); }
    finally { setLoading(false); }
  };

  const openCreateDialog = () => { setEditingProduct(null); setFormData(emptyProduct); setDialogOpen(true); };
  const openEditDialog = (p) => {
    setEditingProduct(p);
    setFormData({ name:p.name, category:p.category, price:p.price.toString(), image:p.image, description:p.description, in_stock:p.in_stock });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const productData = { ...formData, price: parseFloat(formData.price) };
    setLoading(true);
    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData, getAuth());
      } else {
        await axios.post(`${API}/products`, productData, getAuth());
      }
      toast.success(editingProduct ? 'Produto atualizado!' : 'Produto criado!');
      setDialogOpen(false);
      fetchProducts();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) { toast.error('Sessão expirada. Faça login novamente.'); clearSession(); setIsAuthenticated(false); }
      else if (status === 404) { toast.error('Produto não encontrado.'); }
      else {
        // Erro de rede — confirma se operação chegou ao servidor
        toast.info('Verificando operação...');
        setTimeout(async () => { await fetchProducts(); setDialogOpen(false); toast.success(editingProduct ? 'Produto atualizado!' : 'Produto criado!'); }, 1000);
      }
    } finally { setLoading(false); }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Deseja deletar "${product.name}"?`)) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/products/${product.id}`, getAuth());
      toast.success('Produto deletado!'); fetchProducts();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) { toast.error('Sessão expirada.'); clearSession(); setIsAuthenticated(false); }
      else { setTimeout(async () => { await fetchProducts(); toast.success('Produto removido!'); }, 1000); }
    } finally { setLoading(false); }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="admin-login">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-8 shadow-xl">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Lock className="w-8 h-8 text-blue-600 dark:text-blue-400"/>
              </div>
              <h1 className="text-2xl font-secondary font-bold text-slate-900 dark:text-white">Área Administrativa</h1>
              <p className="text-slate-500 dark:text-slate-400 mt-2">Faça login para gerenciar produtos</p>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username">Usuário</Label>
                <Input id="username" type="text" value={credentials.username} onChange={e=>setCredentials(p=>({...p,username:e.target.value}))} placeholder="Digite seu usuário" required data-testid="admin-username"/>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Senha</Label>
                <Input id="password" type="password" value={credentials.password} onChange={e=>setCredentials(p=>({...p,password:e.target.value}))} placeholder="Digite sua senha" required data-testid="admin-password"/>
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white" disabled={loading} data-testid="admin-login-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : 'Entrar'}
              </Button>
            </form>
            <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">Credenciais padrão: admin / fastfix2024</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-20 md:pb-8 pt-4 md:pt-8" data-testid="admin-dashboard">
      {/* Bottom nav — mobile only */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex safe-bottom">
        {[
          {id:'products',      icon:<Package className="w-5 h-5"/>,    label:'Produtos'},
          {id:'sales',         icon:<TrendingUp className="w-5 h-5"/>, label:'Vendas'},
          {id:'pedidos',       icon:<Truck className="w-5 h-5"/>,      label:'Pedidos'},
          {id:'contabilidade', icon:<BookOpen className="w-5 h-5"/>,   label:'Contas'},
        ].map(tab => (
          <button key={tab.id} onClick={()=>handleSetTab(tab.id)}
            className={`flex flex-col items-center justify-center gap-0.5 flex-1 py-2 px-1 text-[10px] font-medium transition-all ${activeTab===tab.id?'text-blue-600 dark:text-blue-400':'text-slate-400 dark:text-slate-500'}`}>
            <span className={`transition-transform ${activeTab===tab.id?'scale-110':''}`}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="container mx-auto px-3 md:px-6 lg:px-8 max-w-7xl">
        <div className="flex items-center justify-between gap-2 mb-4">
          <div>
            <h1 className="text-xl md:text-3xl font-secondary font-bold text-slate-900 dark:text-white">Painel Admin</h1>
            <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400">{products.length} produtos</p>
          </div>
          <div className="flex gap-2">
            {activeTab==='products' && (
              <Button onClick={openCreateDialog} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-product-btn">
                <Plus className="w-4 h-4 md:mr-2"/><span className="hidden md:inline">Novo Produto</span>
              </Button>
            )}
            <Button variant="outline" onClick={handleLogout} data-testid="logout-btn" title="Sair" className="px-2 md:px-4">
              <LogOut className="w-4 h-4"/><span className="hidden md:inline ml-2">Sair</span>
            </Button>
          </div>
        </div>

        {/* Tab bar — desktop only */}
        <div className="hidden md:flex gap-1 mb-6 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl w-fit">
          {[
            {id:'products',      icon:<Package className="w-4 h-4"/>,    label:'Produtos'},
            {id:'sales',         icon:<TrendingUp className="w-4 h-4"/>, label:'Vendas & Lucros'},
            {id:'pedidos',       icon:<Truck className="w-4 h-4"/>,      label:'Pedidos'},
            {id:'contabilidade', icon:<BookOpen className="w-4 h-4"/>,   label:'Contabilidade'},
          ].map(tab => (
            <button key={tab.id} onClick={()=>handleSetTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab===tab.id?'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-sm':'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'}`}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        {loading && products.length===0 ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-600"/></div>
        ) : activeTab==='products' ? (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
            {/* Filtro estoque */}
            <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex gap-2">
              {[
                {id:'todos',    label:'Todos'},
                {id:'estoque',  label:'✅ Em estoque'},
                {id:'esgotado', label:'❌ Esgotado'},
              ].map(f => (
                <button key={f.id} onClick={() => setFiltroEstoque(f.id)}
                  className={`text-xs px-3 py-1.5 rounded-full font-medium transition-colors ${filtroEstoque===f.id ? 'bg-blue-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300'}`}>
                  {f.label}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead><tr><th>Imagem</th><th>Nome</th><th>Categoria</th><th>Preço</th><th>Status</th><th>Ações</th></tr></thead>
                <tbody>
                  {products
                    .filter(p => filtroEstoque === 'todos' || (filtroEstoque === 'estoque' ? p.in_stock : !p.in_stock))
                    .map(product => (
                    <tr key={product.id} data-testid={`product-row-${product.id}`}
                      className={!product.in_stock ? 'opacity-45' : ''}>
                      <td><div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800"><img src={product.image} alt={product.name} className="w-full h-full object-contain p-1"/></div></td>
                      <td><p className="font-medium text-slate-900 dark:text-white line-clamp-1">{product.name}</p></td>
                      <td><span className="text-sm text-slate-600 dark:text-slate-400">{product.category}</span></td>
                      <td><span className="font-semibold text-slate-900 dark:text-white">R$ {product.price.toFixed(2)}</span></td>
                      <td>
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${product.in_stock?'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400':'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'}`}>
                          {product.in_stock?'Em estoque':'Esgotado'}
                        </span>
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="icon" onClick={()=>openEditDialog(product)} className="text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20" data-testid={`edit-product-${product.id}`}><Pencil className="w-4 h-4"/></Button>
                          <Button variant="ghost" size="icon" onClick={()=>handleDelete(product)} className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20" data-testid={`delete-product-${product.id}`}><Trash2 className="w-4 h-4"/></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : activeTab==='sales' ? (
          <SalesTab products={products} auth={credentials}/>
        ) : activeTab==='pedidos' ? (
          <SalesTabWithData products={products} setProducts={setProducts} auth={credentials}/>
        ) : (
          <ContabilidadeWrapper products={products} auth={credentials}/>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editingProduct?'Editar Produto':'Novo Produto'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2"><Label htmlFor="name">Nome do Produto</Label>
                <Input id="name" value={formData.name} onChange={e=>setFormData(p=>({...p,name:e.target.value}))} placeholder="Ex: Carregador Essager 33w" required data-testid="product-name-input"/>
              </div>
              <div className="space-y-2"><Label htmlFor="category">Categoria</Label>
                <Select value={formData.category} onValueChange={v=>setFormData(p=>({...p,category:v}))}>
                  <SelectTrigger data-testid="product-category-select"><SelectValue/></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(cat=><SelectItem key={cat} value={cat}>{cat}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {!editingProduct && (
                <div className="space-y-2"><Label htmlFor="price">Preço de Venda Inicial (R$)</Label>
                  <Input id="price" type="number" step="0.01" value={formData.price} onChange={e=>setFormData(p=>({...p,price:e.target.value}))} placeholder="Ex: 105.00" required data-testid="product-price-input"/>
                  <p className="text-xs text-slate-500">Para atualizar preço e custo, use "📦 Nova Remessa" na aba Vendas & Lucros.</p>
                </div>
              )}
              {editingProduct && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-2 text-xs text-blue-700 dark:text-blue-400">
                  💡 Para atualizar preço e custo, use <strong>📦 Nova Remessa</strong> na aba Vendas & Lucros. O histórico anterior será preservado.
                </div>
              )}
              <div className="space-y-2"><Label htmlFor="image">URL da Imagem</Label>
                <Input id="image" value={formData.image} onChange={e=>setFormData(p=>({...p,image:e.target.value}))} placeholder="https://exemplo.com/imagem.jpg" required data-testid="product-image-input"/>
              </div>
              <div className="space-y-2"><Label htmlFor="description">Descrição</Label>
                <textarea id="description" value={formData.description} onChange={e=>setFormData(p=>({...p,description:e.target.value}))} placeholder="Descrição do produto..."
                  className="flex min-h-[100px] w-full rounded-md border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required data-testid="product-description-input"/>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="in_stock" checked={formData.in_stock} onChange={e=>setFormData(p=>({...p,in_stock:e.target.checked}))} className="w-4 h-4 rounded border-slate-300" data-testid="product-stock-checkbox"/>
                <Label htmlFor="in_stock" className="cursor-pointer">Produto em estoque</Label>
              </div>
              <DialogFooter className="gap-2">
                <Button type="button" variant="outline" onClick={()=>setDialogOpen(false)}>Cancelar</Button>
                <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={loading} data-testid="save-product-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Save className="w-4 h-4 mr-2"/>Salvar</>}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
